import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import { useSendSupportQueryMutation } from '../features/dashboard/dashboardApi';

const SupportCenterWidget: React.FC = () => {
    const [sendSupport] = useSendSupportQueryMutation();

    const [isSupportMinimized, setIsSupportMinimized] = useState(() => {
        return localStorage.getItem('support_minimized') === 'true';
    });
    const [isSupportModalOpen, setIsSupportModalOpen] = useState(false);
    const [supportForm, setSupportForm] = useState({ subject: '', message: '' });
    const [isSubmittingSupport, setIsSubmittingSupport] = useState(false);

    const handleMinimizeSupport = (e: React.MouseEvent) => {
        e.stopPropagation();
        const newState = !isSupportMinimized;
        setIsSupportMinimized(newState);
        localStorage.setItem('support_minimized', String(newState));
    };

    const handleSubmitSupport = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmittingSupport(true);
        try {
            await sendSupport(supportForm).unwrap();
            toast.success('Support query sent! We will get back to you shortly.');
            setIsSupportModalOpen(false);
            setSupportForm({ subject: '', message: '' });
        } catch (err) {
            toast.error('Failed to send support query. Please try again.');
        } finally {
            setIsSubmittingSupport(false);
        }
    };

    return (
        <>
            {/* Sticky Support Center (Industry Standard FAB) */}
            <div className={`fixed bottom-8 right-8 z-50 flex flex-col items-end gap-3 transition-all duration-500 ${isSupportMinimized ? 'translate-y-2' : ''}`}>
                {!isSupportMinimized && (
                    <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 p-4 mb-2 animate-in slide-in-from-bottom-4 duration-300 w-64">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-black text-blue-600 uppercase tracking-widest">Support Center</span>
                            <button onClick={handleMinimizeSupport} className="text-gray-400 hover:text-gray-600">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                        </div>
                        <p className="text-sm font-bold text-gray-900 leading-tight">Need assistance with the portal?</p>
                        <p className="text-[11px] text-gray-500 mt-1 font-medium">Contact your HR administrator directly from here.</p>
                        <button 
                            onClick={() => setIsSupportModalOpen(true)}
                            className="w-full mt-4 bg-blue-600 text-white py-2 rounded-xl text-xs font-bold hover:bg-blue-700 transition-all shadow-lg shadow-blue-100"
                        >
                            Open Support Ticket
                        </button>
                    </div>
                )}
                
                <button 
                    onClick={isSupportMinimized ? handleMinimizeSupport : () => setIsSupportModalOpen(true)}
                    className={`group relative flex items-center justify-center rounded-full shadow-2xl transition-all duration-300 hover:scale-110 active:scale-95 ${
                        isSupportMinimized 
                        ? 'w-12 h-12 bg-gray-900 text-white' 
                        : 'w-16 h-16 bg-blue-600 text-white shadow-blue-200'
                    }`}
                >
                    {isSupportMinimized ? (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    ) : (
                        <svg className="w-8 h-8 animate-in zoom-in duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" /></svg>
                    )}
                    {isSupportMinimized && (
                        <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full border-2 border-white"></span>
                    )}
                </button>
            </div>

            {/* Support Request Modal */}
            {isSupportModalOpen && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center bg-gray-900/60 backdrop-blur-sm p-4 animate-in fade-in duration-200">
                    <div className="bg-white rounded-[2.5rem] w-full max-w-lg shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
                        <div className="p-8 border-b border-gray-50 flex justify-between items-center bg-gray-50/50">
                            <div>
                                <h3 className="text-xl font-black text-gray-900 tracking-tight">noorHR Support</h3>
                                <p className="text-sm text-gray-500 font-medium mt-1">Submit a query to your tenant administrator</p>
                            </div>
                            <button onClick={() => setIsSupportModalOpen(false)} className="p-2 hover:bg-white rounded-xl transition-colors">
                                <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                        </div>
                        
                        <form onSubmit={handleSubmitSupport} className="p-8 space-y-6">
                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Subject</label>
                                <input 
                                    type="text"
                                    required
                                    value={supportForm.subject}
                                    onChange={e => setSupportForm({...supportForm, subject: e.target.value})}
                                    className="w-full px-5 py-4 bg-gray-50 border-none rounded-2xl focus:ring-4 focus:ring-blue-100 transition-all font-bold text-gray-900 placeholder:text-gray-300"
                                    placeholder="What do you need help with?"
                                />
                            </div>

                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Detailed Message</label>
                                <textarea 
                                    required
                                    rows={5}
                                    value={supportForm.message}
                                    onChange={e => setSupportForm({...supportForm, message: e.target.value})}
                                    className="w-full px-5 py-4 bg-gray-50 border-none rounded-2xl focus:ring-4 focus:ring-blue-100 transition-all font-bold text-gray-900 placeholder:text-gray-300 resize-none"
                                    placeholder="Explain your request in detail..."
                                />
                            </div>

                            <div className="flex gap-4 pt-4">
                                <button 
                                    type="button"
                                    onClick={() => setIsSupportModalOpen(false)}
                                    className="flex-1 py-4 rounded-2xl font-black text-gray-500 bg-gray-100 hover:bg-gray-200 transition-all active:scale-95"
                                >
                                    Cancel
                                </button>
                                <button 
                                    type="submit"
                                    disabled={isSubmittingSupport}
                                    className="flex-[2] py-4 rounded-2xl font-black text-white bg-blue-600 shadow-xl shadow-blue-100 hover:bg-blue-700 transition-all active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2"
                                >
                                    {isSubmittingSupport ? (
                                        <>
                                            <svg className="w-5 h-5 animate-spin" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                            </svg>
                                            Sending...
                                        </>
                                    ) : 'Send Query'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </>
    );
};

export default SupportCenterWidget;
